import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTable, usePagination } from 'react-table';
import moment from 'moment-timezone';
import { actionCreators } from './data/uploads/actions';
import getStatusMarkup from './components/getStatusMarkup';
import Badge from '../../../../../../components/Badge';
import onRowClick from './components/onRowClick';
import s from './List.module.scss';
import Tasks from './components/Tasks';

const List = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  const { data, pages, isFetching, hasError } = useSelector(
    state => state.Admin.scenes.Uploads.scenes.list.data.uploads
  );

  const columns = React.useMemo(
    () => [
      {
        Header: "Filnamn",
        accessor: "fileNames",
        Cell: ({ value }) => (
          <>
            {value[0]} 
            {value.length > 1 && (
              <Badge type="primary">+{value.length - 1}</Badge>
            )}
          </>
        )
      },
      {
        Header: "Inskickad av",
        accessor: "sentBy"
      },
      {
        Header: "Mottagare",
        accessor: "recipient"
      },
      {
        Header: "Ankomstdatum",
        accessor: "arrivalDate",
        Cell: ({ value }) => value ? moment(value).format("YYYY-MM-DD") : '-'
      },
      {
        Header: "Raderas",
        accessor: "deletionDate",
        Cell: ({ value }) => value ? moment(value).format("YYYY-MM-DD") : '-'
      },
      {
        Header: "Status",
        accessor: "status",
        Cell: ({ value }) => getStatusMarkup(value)
      },
      {
        Header: "Åtgärder",
        accessor: "tasks",
        Cell: (cell) => <Tasks deleteUpload={deleteUpload} navigate={navigate} {...cell} />
      }
    ],
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data,
      manualPagination: true,
      pageCount: pages,
      initialState: { pageIndex: 0, pageSize: 20 },
    },
    usePagination
  );

  useEffect(() => {
    actionCreators.getFileList({ pageIndex, pageSize })(dispatch);
  }, [pageIndex, pageSize, dispatch]);

  const deleteUpload = (deliveryId) => {
    actionCreators.deleteUpload(deliveryId)(dispatch);
  };

  if (hasError) {
    return <div>Fel vid hämtning av data</div>
  }

  return (
    <div className="page-wrap">
      <div className={"card" + " " + s['upload-list_card']}>
        {isFetching && <div className={s['upload-list_overlay']}>Laddar...</div>}
        <table {...getTableProps()} className={s['upload-list-table']}>
          <thead>
            {headerGroups.map(headerGroup => {
              const { key, ...headerGroupProps } = headerGroup.getHeaderGroupProps();
              return (
                <tr key={key} {...headerGroupProps} className={s['upload-list-table_column']}>
                  {headerGroup.headers.map(column => {
                    const { key, ...columnProps } = column.getHeaderProps();
                    return (
                    <th key={key}{...columnProps} className={s['upload-list-table_columns']}>
                      {column.render('Header')}
                    </th>
                    );
            })}
                </tr>
              );
            })}
          </thead>
          <tbody {...getTableBodyProps()}>
            {page.length === 0 ? (
              <tr><td colSpan={columns.length}>Ingen data att visa</td></tr>
            ) : (
              page.map(row => {
                prepareRow(row);
                const { key, ...rowProps } = row.getRowProps();
                return (
                  <tr key={key} {...rowProps} onClick={() => onRowClick(navigate, row)}>
                    {row.cells.map(cell => {
                      const { key, ...cellProps } = cell.getCellProps();
                      return (
                        <td key={key} {...cellProps}>{cell.render('Cell')}</td>
                      );
                    })}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
        <div className={s.pagination}>
          <div className={s.previous}>
            <button onClick={() => previousPage()} disabled={!canPreviousPage} className={s['pagination-button']}>
              Föregående
            </button>
          </div>
          <div className={s.center}>
            <span className={s.paginationPageOptions}>
              Sida{' '}
              <select
                value={pageIndex}
                className={s.paginationSelect}
                onChange={(e) => gotoPage(Number(e.target.value))}
              >
                {pageOptions.map((_, i) => (
                  <option key={i} value={i}>
                    {i + 1}
                  </option>
                ))}
              </select>{' '}
              av {pageOptions.length}
            </span>

            <span className={s.pageSizeOptions}>
              Rader:{' '}
              <select
                value={pageSize}
                className={s.paginationSelect}
                onChange={(e) => setPageSize(Number(e.target.value))}
              >
                {[20, 25, 50, 100].map(size => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </span>
          </div>
          <div className={s.next}>
            <button onClick={() => nextPage()} disabled={!canNextPage} className={s['pagination-button']}>
              Nästa
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default List;